import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getProxiedAvatarUrl } from '@shared/utils/avatar';

/**
 * User payload shape that can come from various sources (Stytch, API, etc.)
 * Fields may use snake_case (from API) or camelCase (from frontend)
 */
interface UserPayload {
  id: string;
  email: string;
  organization_id?: string;
  organizationId?: string;
  roles?: string[] | unknown;
  permissions?: string[] | unknown;
  permissions_hash?: string;
  permissionsHash?: string;
  given_name?: string;
  family_name?: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  avatar_url?: string;
  picture_url?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  status: 'unauthenticated' | 'authenticated' | 'expired';
  token: string | null;
  isUserResolved: boolean;
  user: {
    id: string;
    email: string;
    organizationId: string;
    roles: string[];
    permissions: string[];
    permissionsHash?: string;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  } | null;
  isLoading: boolean;
  error: string | null;
  sessionMessage: string | null;
  appUserId?: number | null;
  impersonation: {
    active: boolean;
    targetId: string;
    targetName: string;
    targetEmail: string;
    targetOrgId: string;
    targetRoles: string[];
    targetPermissions: string[];
    returnPath?: string;
    token: string;
    expiresAt: string;
    originalToken: string;
    originalUser: AuthState['user'];
  } | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  status: 'unauthenticated',
  token: null,
  isUserResolved: false,
  user: null,
  isLoading: false,
  error: null,
  sessionMessage: null,
  appUserId: null,
  impersonation: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setSession: (
      state,
      action: PayloadAction<{
        token: string;
        user?: UserPayload;
        message?: string;
        userResolved?: boolean;
      }>
    ) => {
      state.isAuthenticated = true;
      state.status = 'authenticated';
      state.token = action.payload.token;
      state.isUserResolved = action.payload.userResolved ?? !!action.payload.user;
      state.isLoading = false;
      state.error = null;
      state.sessionMessage = action.payload.message ?? null;

      if (action.payload.user) {
        const u = action.payload.user;
        const firstName = u.given_name || u.firstName || '';
        const lastName = u.family_name || u.lastName || '';
        // Use proxied avatar URL to avoid Google CDN rate limits
        const avatarUrl =
          getProxiedAvatarUrl(u.id, action.payload.token) ||
          u.avatarUrl ||
          u.avatar_url ||
          u.picture_url ||
          '';
        state.user = {
          id: u.id,
          email: u.email,
          organizationId: u.organization_id || u.organizationId || '',
          roles: Array.isArray(u.roles) ? u.roles : [],
          permissions: Array.isArray(u.permissions) ? u.permissions : [],
          permissionsHash: u.permissions_hash || u.permissionsHash,
          firstName,
          lastName,
          avatarUrl: avatarUrl || undefined,
        };
      } else {
        state.user = null;
      }
    },
    setUserProfile: (state, action: PayloadAction<{ user: UserPayload }>) => {
      const u = action.payload.user;
      if (!u) return;
      state.isAuthenticated = true;
      state.status = 'authenticated';
      state.isUserResolved = true;
      state.sessionMessage = null;
      const firstName = u.firstName || u.given_name || '';
      const lastName = u.lastName || u.family_name || '';
      // Use avatarUrl from payload if provided, otherwise fall back to other fields
      // Avatar URL should be computed before dispatching this action (e.g., in AuthProvider)
      const avatarUrl = u.avatarUrl || u.avatar_url || u.picture_url || '';
      state.user = {
        id: u.id,
        email: u.email,
        organizationId: u.organizationId || u.organization_id || '',
        roles: Array.isArray(u.roles) ? u.roles : [],
        permissions: Array.isArray(u.permissions) ? u.permissions : [],
        permissionsHash: u.permissions_hash || u.permissionsHash,
        firstName,
        lastName,
        avatarUrl: avatarUrl || undefined,
      };
    },
    clearSession: (state) => {
      state.isAuthenticated = false;
      state.status = 'unauthenticated';
      state.token = null;
      state.isUserResolved = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.sessionMessage = null;
      state.appUserId = null;
      state.impersonation = null;
    },
    expireSession: (state, action: PayloadAction<{ message?: string } | undefined>) => {
      state.isAuthenticated = false;
      state.status = 'expired';
      state.token = null;
      state.isUserResolved = false;
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.sessionMessage =
        action?.payload?.message ?? 'Your session has expired. Please sign in again.';
      state.appUserId = null;
      state.impersonation = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setAppUserId: (state, action: PayloadAction<number | null>) => {
      state.appUserId = action.payload;
    },
    setUserResolved: (state, action: PayloadAction<boolean>) => {
      state.isUserResolved = action.payload;
    },
    startImpersonation: (
      state,
      action: PayloadAction<{
        targetId: string;
        targetName: string;
        targetEmail: string;
        targetOrgId: string;
        targetRoles: string[];
        targetPermissions: string[];
        returnPath?: string;
        token: string;
        expiresAt: string;
        originalToken: string;
        originalUser: AuthState['user'];
      }>
    ) => {
      state.impersonation = {
        active: true,
        ...action.payload,
      };
    },
    endImpersonation: (state) => {
      state.impersonation = null;
    },
  },
});

export const {
  setLoading,
  setSession,
  setUserProfile,
  clearSession,
  expireSession,
  setError,
  setAppUserId,
  setUserResolved,
  startImpersonation,
  endImpersonation,
} = authSlice.actions;
export default authSlice.reducer;
